import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { formatBRL, cn } from "@/lib/utils";
import type { SalesRepPerformance } from "@/data/types";

export function SellerPerformance({ period }: { period: { periodStart: string; periodEnd: string } }) {
  const q = useQuery({
    queryKey: ["sales-reps", period],
    queryFn: () => client.getSalesReps(period),
  });

  const reps = q.data ?? [];
  const sorted = [...reps].sort((a, b) => b.amountInPeriod - a.amountInPeriod);
  const topAmount = sorted[0]?.amountInPeriod ?? 0;

  return (
    <section className="rounded-lg border border-border-subtle bg-bg-card p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">Desempenho dos Vendedores</h2>
          <p className="text-[13px] text-text-muted">Reuniões, vendas e acumulado no período filtrado</p>
        </div>
        <span className="text-xs text-text-muted">{reps.length} vendedores ativos</span>
      </div>

      <div className="overflow-hidden rounded-md border border-border-subtle">
        <table className="w-full">
          <thead className="bg-bg-secondary">
            <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">
              <th className="px-5 py-3 text-left">Vendedor</th>
              <th className="px-5 py-3 text-right">Reuniões</th>
              <th className="px-5 py-3 text-right">Vendas</th>
              <th className="px-5 py-3 text-right">Valor no período</th>
              <th className="px-5 py-3 text-right">Acumulado</th>
              <th className="px-5 py-3 text-left">Performance</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-sm text-text-muted">
                  Carregando…
                </td>
              </tr>
            )}
            {sorted.map((r, idx) => (
              <SellerRow key={r.userId} rep={r} rank={idx + 1} barRatio={topAmount ? r.amountInPeriod / topAmount : 0} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SellerRow({
  rep,
  rank,
  barRatio,
}: {
  rep: SalesRepPerformance;
  rank: number;
  barRatio: number;
}) {
  return (
    <tr className="border-t border-border-subtle text-sm transition hover:bg-bg-card-hover">
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="w-4 text-right text-xs font-bold text-text-muted">{rank}</span>
          <span
            className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-text-on-accent"
            style={{ backgroundColor: rep.avatarColor }}
          >
            {getInitials(rep.name)}
          </span>
          <div>
            <div className="font-medium text-text-primary">{rep.name}</div>
            <div className="text-xs text-text-muted">
              Conversão {rep.conversionRate.toFixed(1).replace(".", ",")}%
            </div>
          </div>
        </div>
      </td>
      <td className="px-5 py-3 text-right font-medium text-text-secondary">{rep.meetingsHeld}</td>
      <td className="px-5 py-3 text-right font-medium text-text-secondary">{rep.salesCount}</td>
      <td className="px-5 py-3 text-right font-semibold text-text-primary">{formatBRL(rep.amountInPeriod)}</td>
      <td className="px-5 py-3 text-right text-text-secondary">{formatBRL(rep.amountAccumulated)}</td>
      <td className="px-5 py-3">
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-bg-primary">
          <div
            className={cn("h-full rounded-full bg-brand-amber transition-all")}
            style={{ width: `${Math.max(4, barRatio * 100)}%` }}
          />
        </div>
      </td>
    </tr>
  );
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
